import { useState } from "react";
import {
  Box,
  Typography,
  Skeleton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import images from "../../../assets/images";
import useUserStore from "../../../stores/userStore";
import { ButtonOutlined } from "../../../components/button-outlined/button-outlined";
import { AnimatePresence, motion } from "framer-motion";
import useAppStore from "../../../stores/appStore";

type BotKey = "Robbi" | "Alice" | "Mario" | "Endy" | "Fatima";

const botImageMap: Record<BotKey, string> = {
  Robbi: images.OnboardingBot,
  Alice: images.OnboardingBotAlice,
  Mario: images.OnboardingBotMario,
  Endy: images.OnboardingBotEndy,
  Fatima: images.OnboardingBotFatima,
};

const botDisplayNameMap: Record<BotKey, string> = {
  Robbi: "Robbi",
  Alice: "Alice",
  Mario: "Mario",
  Endy: "Endy",
  Fatima: "Fatima",
};

interface ChooseOnboardingProps {}

export const ChooseOnboarding = ({}: ChooseOnboardingProps) => {
  const { user, completeOnboarding } = useUserStore();
  const { updateBotAvatar } = useAppStore();
  const [selectedBot, setSelectedBot] = useState<BotKey>("Robbi");
  const [allImagesLoaded, setAllImagesLoaded] = useState<boolean>(false);
  const [loadedCount, setLoadedCount] = useState<number>(0);

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const imagesToPreload = [
    images.OnboardingBot,
    images.OnboardingBotAlice,
    images.OnboardingBotMario,
    images.OnboardingBotEndy,
    images.OnboardingBotFatima,
  ];

  const handleImageLoad = () => {
    setLoadedCount((prev) => {
      const newCount = prev + 1;
      if (newCount === imagesToPreload.length) {
        setAllImagesLoaded(true);
      }
      return newCount;
    });
  };

  const handleSave = async () => {
    await updateBotAvatar(selectedBot);
    await completeOnboarding();
  };

  return (
    <Box
      sx={{
        position: "relative",
        height: "100dvh",
        background: "#0B0E1D",
        overflowY: "hidden",
        overflowX: "hidden",
        display: "flex",
        flexDirection: {
          xs: "column-reverse",
          sm: "column-reverse",
          md: "row",
        },
        justifyContent: "space-between",
        width: "100%",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: { xs: "90%", md: "50%" },
          background: "#FFFFFF",
          borderRadius: { xs: "30px", md: "40px" },
          marginX: { xs: "auto", md: "56px" },
          marginBottom: { xs: "16px", md: 0 },
          paddingY: { xs: "12px", md: "64px" },
          zIndex: 20,
          position: "relative",
          maxHeight: { xs: "70vh", md: "auto" },
          overflowY: { xs: "auto", md: "visible" },
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            paddingX: { xs: "12px", md: "25px" },
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1, md: 2 },
            alignItems: "center",
            paddingBottom: { xs: "8px", md: 0 },
          }}
        >
          {(["Robbi", "Alice", "Mario", "Endy", "Fatima"] as BotKey[]).map(
            (bot) => (
              <Box
                key={bot}
                onClick={() => setSelectedBot(bot)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  width: "100%",
                  border:
                    selectedBot === bot
                      ? "2px solid #06B6D4"
                      : "2px solid #C5C5C5",
                  borderRadius: "12px",
                  cursor: "pointer",
                  p: { xs: 0.6, md: 1 },
                  flexShrink: 0,
                }}
              >
                {!allImagesLoaded && (
                  <Skeleton
                    variant="circular"
                    width={50}
                    height={50}
                    sx={{ marginRight: "10px" }}
                  />
                )}
                <img
                  loading="eager"
                  src={images[bot]}
                  alt={botDisplayNameMap[bot]}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    display: allImagesLoaded ? "block" : "none",
                    objectFit: "cover",
                  }}
                  onLoad={handleImageLoad}
                />
                <Typography
                  sx={{
                    fontSize: { xs: "15px", md: "18px" },
                    fontWeight: 600,
                    color: "#0E0E0E",
                  }}
                >
                  {botDisplayNameMap[bot]}
                </Typography>
              </Box>
            )
          )}
          <ButtonOutlined
            variant="outlined"
            onClick={handleSave}
            sx={{
              borderRadius: "20px",
              border: "1px solid #06B6D4",
              color: "#06B6D4",
              textTransform: "none",
              fontWeight: 500,
              width: "100%",
              px: { xs: 3, md: 4 },
              py: { xs: 1.75, md: 2.5 },
              lineHeight: 0.75,
              flexShrink: 0,
              mt: { xs: 0.5, md: 0 },
              "&:hover": {
                backgroundColor: "#28C9DC1A",
                borderColor: "#06B6D4",
              },
            }}
          >
            Save
          </ButtonOutlined>
        </Box>
      </Box>

      {!isMdUp && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: { xs: "50%", md: "80%" },
            zIndex: 5,
            pointerEvents: "none",
            backgroundImage: {
              xs: `linear-gradient(
               to top,
               rgba(11, 14, 29, 1) 20%,
               rgba(11, 14, 29, 0) 60%
             )`,
              sm: `linear-gradient(
               to top,
               rgba(11, 14, 29, 1) 20%,
               rgba(11, 14, 29, 0) 65%
             )`,
            },
            backgroundRepeat: "no-repeat",
          }}
        />
      )}

      <Box
        sx={{
          position: "relative",
          width: { xs: "100%", sm: "100%", md: "50%" },
          height: { xs: "45%", sm: "50%", md: "100%" },
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: { xs: 5, sm: 20, md: 210 },
            left: { xs: -10, sm: 20, md: -55 },
            zIndex: 15,
          }}
        >
          <Box
            component="img"
            src={images.ChooseOnboardingWelcomeMsg}
            sx={{
              position: "relative",
              marginLeft: "10px",
              width: {
                xs: "160px",
                sm: "160px",
                md: "200px",
              },
              height: {
                xs: "120px",
                sm: "120px",
                md: "150px",
              },
            }}
          />
        </Box>

        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 1,
            overflow: "hidden",
          }}
        >
          <AnimatePresence mode="wait">
            {!allImagesLoaded ? (
              <Skeleton
                variant="rectangular"
                width="100%"
                height="100%"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  borderRadius: "12px",
                }}
              />
            ) : (
              <motion.img
                key={selectedBot}
                src={botImageMap[selectedBot]}
                alt={botDisplayNameMap[selectedBot]}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={
                  isMdUp
                    ? {
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        zIndex: 1,
                      }
                    : {
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        maxHeight: "45vh",
                        zIndex: 1,
                      }
                }
              />
            )}
          </AnimatePresence>
        </Box>

        {isMdUp && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: "none",
              zIndex: 10,
              background: {
                xs: `linear-gradient(
                to top,
                rgba(11, 14, 29, 1) 10%,
                rgba(11, 14, 29, 0) 25%
              )`,
                sm: `linear-gradient(
                to top,
                rgba(11, 14, 29, 1) 10%,
                rgba(11, 14, 29, 0) 25%
              )`,
                md: `linear-gradient(
                to right,
                rgba(11, 14, 29, 1) 0%,
                rgba(11, 14, 29, 0) 40%
              )`,
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
};
